import React, { useEffect, useMemo, useState } from 'react';
import SearchSelect from './SearchSelect';
import { Field, FilePicker, InfoTip } from './FormKit';
import { formatDate } from '../../utils/dateFormat';
import { FieldInvalidInline } from './formValidation';
import { FieldCommentInline } from '../workflow/fieldComments';
import { bizFileRequired, entityCategoryLabel } from './entityCategory';
import { fetchJSON } from '../../utils/api';
import { activeServiceLines, fieldsForCompany } from './EngagementFormFields';
import { lineFeeKey, isLineRowKey, serviceSubtitle } from '../../utils/serviceLines';

/**
 * The "which companies" half of an engagement-creating form — ONE definition
 * for the two surfaces that create engagements from the client master:
 *
 *   WORKFLOW → Engagement Registration      (New page + edit page)
 *   Management → Engagements → New          (/qm/engagements/new)
 *
 * Both pick a client group, tick every company of that group the project
 * covers (one engagement each, plus one per service line added under a
 * company), and attach one BizFile per ticked company.
 * The shape is Registration's: `groupId` (client group id) + `entityIds`
 * (ticked company ids). Only companies with a UEN are offered — an engagement
 * name is "Entity - UEN - FYE", and the server refuses a company without one.
 *
 * Also here: the live "one engagement per company per FYE" guard. Each
 * surface asks its own endpoint (the Management one is gated by the
 * engagements key, the Registration one by the submit key), but both run the
 * same server lookup and return the same sentence.
 */

// ── BizFile ────────────────────────────────────────────────────────────────

export const BIZFILE_DOC_TYPE = 'BIZFILE';
export const bizFileOf = (files, entityId) =>
  (files || []).find((x) => x.docType === BIZFILE_DOC_TYPE && x.entityId === entityId) || null;

// The marking key of one company's BizFile row — per company, like the file itself.
export const bizFileKey = (entityId) => `bizfile_${entityId}`;

/**
 * BizFiles picked in this session, keyed by company and PRUNED whenever the
 * ticked companies change.
 *
 * Without the pruning, unticking a company leaves its file in the map: the row
 * vanishes from the screen but the file is still posted, and the save dies on
 * "A BizFile was uploaded for a company that is not on this registration" — an
 * error about a company the form no longer shows. Each row's
 * <input type="file"> is keyed by company id, so re-ticking one renders an
 * empty picker; dropping the file keeps what is stored and what is on screen
 * saying the same thing.
 */
export const useBizFiles = (entityIds) => {
  const [bizFiles, setBizFiles] = useState({});
  // A string key so the effect only fires when the SET of companies changes,
  // not on every re-render of a new array with the same ids.
  const key = [...(entityIds || [])].sort((a, b) => a - b).join(',');
  useEffect(() => {
    const kept = new Set(key ? key.split(',') : []);
    setBizFiles((prev) => {
      const next = Object.fromEntries(Object.entries(prev).filter(([id]) => kept.has(String(id))));
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [key]);
  return [bizFiles, setBizFiles];
};

/**
 * One BizFile row per ticked company. `required` is the surface's rule:
 * Registration cannot be submitted without every BizFile; Management writes
 * live records (historical engagements included) and files whatever exists.
 * It ANDs with the company's own rule — a Government Agency or an
 * overseas company has no ACRA BizFile, so its row is the optional
 * "Registration document" slot (shared/entityCategory.bizFileRequired).
 * `existing` names a company's already-attached BizFile on its row (the
 * Registration edit page), where picking a file replaces it.
 */
export const BizFileUploads = ({ f, options, bizFiles, setBizFiles, existing = [], required = true }) => {
  const entities = (options || {}).entities || [];
  const entityOf = (id) => entities.find((e) => e.id === id) || {};
  const nameOf = (id) => entityOf(id).entityName || `Company #${id}`;
  const pick = (entityId, file) => setBizFiles({ ...bizFiles, [entityId]: file || null });

  if ((f.entityIds || []).length === 0) {
    return <p className="text-sm text-amber-700">Pick the companies above first — each one attaches its own BizFile.</p>;
  }
  return (
    <>
      {f.entityIds.map((entityId) => {
        const current = bizFileOf(existing, entityId);
        const picked = bizFiles[entityId];
        const acra = bizFileRequired(entityOf(entityId).entityCategory);
        return (
          <Field
            key={entityId}
            field={bizFileKey(entityId)}
            label={acra ? `BizFile — ${nameOf(entityId)}` : `Registration Document — ${nameOf(entityId)}`}
            required={required && acra}
            hint={current
              ? `Currently attached: ${current.originalName}. Choosing a file replaces it.`
              : acra
                ? 'PDF only, up to 30 MB.'
                : `${entityCategoryLabel(entityOf(entityId).entityCategory)} — no ACRA BizFile; attach its registration document if there is one. PDF only, up to 30 MB.`}
          >
            <FilePicker accept=".pdf"
              onChange={(e) => pick(entityId, (e.target.files || [])[0])}
              files={picked || null} />
          </Field>
        );
      })}
    </>
  );
};

// ── Duplicate guard ────────────────────────────────────────────────────────

/**
 * The "one engagement per company per FYE" guard, asked live — the warning
 * appears as soon as the companies and FYE are picked, not only on submit.
 * Fires once both halves of the question are answered, debounced so ticking
 * four companies is one call.
 *
 * The server is still the authority — the write path re-runs the same check
 * and returns the same sentence. A failed lookup therefore reports nothing
 * rather than guessing: silence costs a late error message, a wrong warning
 * costs trust in the warning.
 *
 * @param {string} endpoint    the surface's duplicate-check URL
 * @param {number[]} entityIds ticked companies
 * @param {string} yearEnd     FYE (YYYY-MM-DD)
 * @param {number} [requestId] Registration modify path — that request's own companies are exempt
 * @param {object} [service]   { service, serviceOther } — the Service Provided answer;
 *                             the slot is company + FYE + service, so the question
 *                             has three halves and is re-asked when the service changes;
 *                             `excludeEngagementId` — an existing engagement being moved,
 *                             which never clashes with itself (useEngagementSlotCheck)
 */
export const useDuplicateEngagements = (endpoint, entityIds, yearEnd, requestId, {
  service = '', serviceOther = '', description = '', excludeEngagementId = null,
  // Rows with a service / description of their own — a company's answer under
  // "Assign per entity", every service line: [{ key, entityId, service,
  // serviceOther, description }], each asked as its own slot and answered
  // with its `key` as `rowKey` (EngagementFormFields.slotQuestionOf).
  rows = [],
} = {}) => {
  // { duplicates, notices } — duplicates block the submit; notices only tell
  // (a service that allows several engagements per company and FYE: how many
  // the company already has, the new one being numbered on creation).
  const [answer, setAnswer] = useState({ duplicates: [], notices: [] });
  // Everything the query needs lives in the key, so the effect's deps are
  // complete and it re-runs exactly when the question changes.
  const key = JSON.stringify({
    endpoint,
    ids: [...(entityIds || [])].sort((a, b) => a - b).join(','),
    fye: yearEnd || '',
    reqId: requestId || '',
    engId: excludeEngagementId || '',
    service: service || '',
    serviceOther: serviceOther || '',
    // Flagged services: a repeated Description of Service in the same slot is
    // a clash too, so the description is part of the question.
    description: String(description || '').trim(),
    rows: (rows || []).filter((r) => r && r.entityId && (r.service === 'Other' ? r.serviceOther : r.service)),
  });

  useEffect(() => {
    const { endpoint: url, ids, fye, reqId, engId, service: svc, serviceOther: svcOther, description: desc, rows: own } = JSON.parse(key);
    const empty = { duplicates: [], notices: [] };
    if (!fye || (!(ids && svc) && own.length === 0)) { setAnswer(empty); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const qs = new URLSearchParams({ entityIds: ids, yearEnd: fye });
      if (reqId) qs.set('requestId', reqId);
      if (engId) qs.set('engagementId', engId);
      if (svc) qs.set('service', svc);
      if (svcOther) qs.set('serviceOther', svcOther);
      if (desc) qs.set('description', desc);
      if (own.length > 0) qs.set('rows', JSON.stringify(own));
      try {
        const data = await fetchJSON(`${url}?${qs.toString()}`);
        if (!cancelled) setAnswer({ duplicates: data.duplicates || [], notices: data.notices || [] });
      } catch { if (!cancelled) setAnswer(empty); }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [key]);

  return answer;
};

/**
 * The same question for an EXISTING engagement being edited (Engagement
 * Amendment tabs, Management → Edit): asked only once the edit moves it to
 * another company, FYE or service, or changes its Description of Service —
 * an untouched engagement is never warned about its own slot. The
 * engagement itself never counts as a clash.
 *
 * @param {string} endpoint   the surface's duplicate-check URL
 * @param {object} engagement the live engagement ({ id, entityId, yearEnd, serviceProvided, descriptionOfService })
 * @param {object} f          the form ({ entityId, yearEnd, service, serviceOther, descriptionOfService })
 */
export const useEngagementSlotCheck = (endpoint, engagement, f) => {
  const lower = (v) => String(v || '').trim().toLowerCase();
  const service = f.service === 'Other' ? String(f.serviceOther || '').trim() : String(f.service || '').trim();
  const day = (v) => String(v || '').slice(0, 10);
  const touched = !!engagement && (
    String(f.entityId ?? '') !== String(engagement.entityId ?? '')
    || day(f.yearEnd) !== day(engagement.yearEnd)
    || lower(service) !== lower(engagement.serviceProvided)
    || String(f.descriptionOfService || '').trim() !== String(engagement.descriptionOfService || '').trim()
  );
  return useDuplicateEngagements(endpoint, touched && f.entityId ? [f.entityId] : [], f.yearEnd, null, {
    service: f.service, serviceOther: f.serviceOther, description: f.descriptionOfService,
    excludeEngagementId: engagement ? engagement.id : null,
  });
};

/**
 * The warning — the rule once, then one short line per company.
 * The company name plus what is holding it is the whole message; a composed
 * engagement name and an "untick or change the FYE" instruction would repeat
 * what the row above already shows.
 *
 * The server's full sentence still travels on each row (`message`) — it is what
 * the submit error says, so the two surfaces stay one voice even though the
 * banner shows the short form.
 */
const DESCRIPTION_CODES = new Set(['DESCRIPTION_REQUIRED', 'DUPLICATE_DESCRIPTION']);
const isDescriptionClash = (d) => DESCRIPTION_CODES.has(d.code);

// A service line's row reads with its service; a company's own slot is the
// company alone (its service is the row above).
const subject = (d) => (d.rowKey && isLineRowKey(d.rowKey) ? `${d.entityName} (${d.service})` : d.entityName);
const clashLine = (d) => (d.kind === 'request'
  ? `${subject(d)} — already on ${d.requestNo}, in review`
  : `${subject(d)} — already has an engagement`);

// The answers about a company's own slot (no row key, or an own: one) — the
// service lines' answers sit under their own rows (EngagementFormFields).
const ownRows = (list = []) => list.filter((d) => !d.rowKey || !isLineRowKey(d.rowKey));
/** The answers about one row of the live check (`rowKey`). */
export const rowsFor = (list = [], rowKey) => list.filter((d) => d.rowKey === rowKey);

/**
 * Whether the live duplicate-check has turned Description of Service
 * mandatory: a company already holds (or has queued) an engagement of this
 * service for the FYE — with no description yet (DESCRIPTION_REQUIRED) or
 * with one that already lets the newcomer in (a `notice`).
 */
export const descriptionNeededFrom = (duplicates = [], notices = []) =>
  ownRows(duplicates).some((d) => d.code === 'DESCRIPTION_REQUIRED') || ownRows(notices).length > 0;

/**
 * Under the FYE (section 1): only the hard slot clashes — the ones no field
 * on this form can resolve. Everything about the Description of Service
 * (required, repeated, numbering) is said next to that field instead
 * (DescriptionOfServiceNotice), where the person can act on it.
 */
export const DuplicateEngagementWarning = ({ duplicates = [] }) => {
  const hard = duplicates.filter((d) => !isDescriptionClash(d));
  if (hard.length === 0) return null;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
      <p className="text-sm font-medium text-amber-800">One engagement per company per FYE per service</p>
      <ul className="mt-1 space-y-0.5">
        {hard.map((d) => (
          <li key={d.rowKey || `own:${d.entityId}`} className="text-sm text-amber-700">{clashLine(d)}</li>
        ))}
      </ul>
    </div>
  );
};

// One company ticked: its name is the row above, so the sentence stands alone;
// several: name the ones concerned.
const lead = (rows) => (rows.length > 1 ? `${rows.map((r) => r.entityName).join(', ')} — ` : '');

/**
 * What the live check says about the Description of Service of one slot —
 * { text, tone } (amber blocks the submit, slate only tells) or null.
 * `named` lists the companies when several are concerned (the shared field);
 * a service line's own notice stands alone under its row.
 */
export const descriptionNoticeOf = (duplicates = [], notices = [], { named = true } = {}) => {
  const twins = duplicates.filter((d) => d.code === 'DUPLICATE_DESCRIPTION');
  const needs = duplicates.filter((d) => d.code === 'DESCRIPTION_REQUIRED');
  const who = (rows) => (named ? lead(rows) : '');
  if (twins.length > 0) {
    return { tone: 'amber', text: `${who(twins)}this Description of Service is already used by another engagement of this service for this FYE — describe this one differently.` };
  }
  if (needs.length > 0) {
    return { tone: 'amber', text: `${who(needs)}already has an engagement of this service for this FYE — add a Description of Service to tell this one apart.` };
  }
  if (notices.length > 0) {
    const n = notices[0].existing || 1;
    return { tone: 'slate', text: `${who(notices)}already has ${n} engagement${n === 1 ? '' : 's'} of this service for this FYE — this one will be numbered on creation.` };
  }
  return null;
};

/** One short line, amber when it blocks the submit, grey when it only tells. */
export const NoticeLine = ({ notice }) => {
  if (!notice || !notice.text) return null;
  const cls = notice.tone === 'amber'
    ? 'rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800'
    : 'rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600';
  return <p className={cls}>{notice.text.charAt(0).toUpperCase() + notice.text.slice(1)}</p>;
};

/**
 * Under the Service Provided / Description of Service row (section 2): what
 * the live check says about the companies' own slots.
 */
export const DescriptionOfServiceNotice = ({ duplicates = [], notices = [] }) => (
  <NoticeLine notice={descriptionNoticeOf(ownRows(duplicates), ownRows(notices))} />
);

// ── Company picker ─────────────────────────────────────────────────────────

export const COMPANIES_SECTION_HINT = (
  <>Pick the client group, then tick every company this project covers (one engagement each). <InfoTip text="Register missing companies through New Client first." /></>
);

// Companies of the chosen group that can hold an engagement (they have a UEN).
export const groupCompanies = (entities, groupId) => {
  if (!groupId) return [];
  return (entities || []).filter((e) => String(e.clientGroupId) === String(groupId) && e.uen);
};

/**
 * Client group select + the company checklist. Writes `groupId` and
 * `entityIds` on the form; changing the group clears the ticks (the ticks are
 * defined as "companies of this group"). The checklist is not a Field, so the
 * "Required" mark and a reviewer's comment on the Companies entry are wired
 * here to sit right under it; outside a comments provider they render nothing.
 *
 * `CompanyChecklistToolbar` is the list's header line: the caption on the
 * left, a "n of m selected" count and Select All / Clear All on the right.
 * Select All ticks every company of the group; Clear All unticks them all.
 * Each disappears when it would do nothing (all ticked / none ticked).
 */
export const CompanyChecklistToolbar = ({ caption, total, selected, onSelectAll, onClearAll }) => (
  <div className="flex items-center gap-3 mb-1.5">
    <p className="text-xs text-slate-500 min-w-0">{caption}</p>
    <span className="ml-auto text-xs text-slate-500 whitespace-nowrap">
      <span className="font-semibold text-slate-700">{selected}</span> of {total} selected
    </span>
    {total > 1 && selected < total && (
      <button type="button" onClick={onSelectAll} className="text-xs text-blue-600 hover:text-blue-700 whitespace-nowrap">
        Select All
      </button>
    )}
    {selected > 0 && (
      <button type="button" onClick={onClearAll} className="text-xs text-slate-500 hover:text-slate-700 whitespace-nowrap">
        Clear All
      </button>
    )}
  </div>
);

export const CompanyChecklist = ({ f, set, options }) => {
  const { groups = [], entities = [] } = options || {};
  const companies = useMemo(() => groupCompanies(entities, f.groupId), [entities, f.groupId]);
  const toggleEntity = (id) => set('entityIds', f.entityIds.includes(id)
    ? f.entityIds.filter((x) => x !== id)
    : [...f.entityIds, id]);

  return (
    <>
      <Field label="Client Group" required field="clientGroup">
        <SearchSelect
          value={f.groupId}
          onChange={(v) => { set('groupId', v); set('entityIds', []); }}
          options={groups.map((g) => ({ value: g.id, label: g.name }))}
          placeholder="Search group…"
        />
      </Field>
      {f.groupId && (
        companies.length === 0 ? (
          <p className="text-sm text-amber-700">This group has no registered companies with a UEN yet — register them through New Client first.</p>
        ) : (
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
            <CompanyChecklistToolbar
              caption="Companies (Client's Name + UEN, from Clients & Entities):"
              total={companies.length}
              selected={companies.filter((e) => f.entityIds.includes(e.id)).length}
              onSelectAll={() => set('entityIds', companies.map((e) => e.id))}
              onClearAll={() => set('entityIds', [])}
            />
            <ul className="space-y-1">
              {companies.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={f.entityIds.includes(e.id)} onChange={() => toggleEntity(e.id)} />
                  <span>{e.entityName}</span>
                  <span className="text-xs text-slate-500 font-mono">{e.uen}</span>
                  {/* The Date of Appointment comes from the client master — read it
                      here, where the companies are picked; it is not asked below. */}
                  {e.dateOfAppointment && <span className="text-xs text-slate-500">· appointed {formatDate(e.dateOfAppointment)}</span>}
                </li>
              ))}
            </ul>
          </div>
        )
      )}
      <FieldInvalidInline field="entityNames" text="Required — tick at least one company" />
      <FieldCommentInline field="entityNames" />
    </>
  );
};

// ── Fee rows ───────────────────────────────────────────────────────────────

/**
 * The fee rows of a form: one per ticked company, then one per service line
 * (key lineFeeKey(line.k)). Two or more rows ⇒ every row enters its own
 * amount. The per-company profile answers keep the companies only.
 *
 * Every row carries the same two lines the QC form's fee rows use — `title`
 * the company, `subtitle` its "Service · description" — so a company's own
 * engagement reads like its lines instead of standing there as a bare name
 * (`name` joins the two for labels that take one string). A company's service
 * and description are the form's, or its own where it answers them itself.
 */
export const feeRowsOf = (f, options) => {
  const entities = (options || {}).entities || [];
  const nameOf = (id) => (entities.find((e) => e.id === id) || {}).entityName || `Company #${id}`;
  const row = (id, title, subtitle) => ({ id, title, subtitle, name: subtitle ? `${title} — ${subtitle}` : title });
  return [
    ...(f.entityIds || []).map((id) => row(id, nameOf(id), serviceSubtitle(fieldsForCompany(f, id)))),
    ...activeServiceLines(f).map((l) => row(
      lineFeeKey(l.k), nameOf(l.entityId), serviceSubtitle(l) || 'Additional service'
    )),
  ];
};

// The company half of the required check — `[{ key, label }]`, keys matching
// the controls above. No group yet ⇒ the group picker is the gap; the company
// checklist is not asked for until it is on screen.
export const companyRequiredGaps = (f) => {
  const gaps = [];
  if (!f.groupId) gaps.push({ key: 'clientGroup', label: 'Client Group' });
  else if ((f.entityIds || []).length === 0) gaps.push({ key: 'entityNames', label: 'Company (At Least One)' });
  return gaps;
};
